import type { ReactNode } from 'react'
import { RotateCw, TriangleAlert } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import {
  CHAT_COPY,
  conversationRowState,
  hasFailedSend,
  useChatOutboxStore,
  useChatUserId,
  type ChatConversation,
  type ChatTriage,
} from '@/entities/chat'
import { ConversationRow } from './ConversationRow'

export function ConversationList({
  conversations,
  selectedId,
  onSelect,
  isLoading,
  error,
  onRetry,
  triage,
  emptyAction,
  kindHint,
}: {
  conversations: ChatConversation[]
  selectedId: number | null
  onSelect: (id: number) => void
  isLoading: boolean
  error: unknown
  onRetry: () => void
  triage: ChatTriage
  emptyAction?: ReactNode
  kindHint: 'chat' | 'ops'
}) {
  const myUserId = useChatUserId()
  const outbox = useChatOutboxStore((state) => state.outbox)

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-8 text-center">
        <TriangleAlert className="h-6 w-6 text-bad" strokeWidth={1.6} />
        <p className="text-[13px] font-medium text-ink">{CHAT_COPY.listFailed}</p>
        <Button variant="outline" size="sm" className="mt-1" onClick={onRetry}>
          <RotateCw className={cn('h-3.5 w-3.5')} />
          {CHAT_COPY.retry}
        </Button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="flex items-start gap-2.5">
            <Skeleton className="h-[34px] w-[34px] shrink-0 rounded-lg" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (conversations.length === 0) {
    const isUnreadTriage = triage === 'unread'
    const emptyMessage = isUnreadTriage
      ? CHAT_COPY.dockNothingUnread
      : kindHint === 'chat'
        ? CHAT_COPY.noConversationsChat
        : emptyAction
          ? CHAT_COPY.noConversationsAction
          : CHAT_COPY.noConversations
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-10 text-center text-[13px] text-ink-3">
        <p>{emptyMessage}</p>
        {!isUnreadTriage && emptyAction}
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {conversations.map((conversation) => (
        <ConversationRow
          key={conversation.id}
          conversation={conversation}
          active={conversation.id === selectedId}
          deliveryState={conversationRowState(conversation, myUserId, hasFailedSend(outbox, conversation.id))}
          myUserId={myUserId}
          onSelect={() => onSelect(conversation.id)}
        />
      ))}
    </div>
  )
}
